import { Link } from "wouter";
import { useId, useMemo, useState } from "react";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SharePostRow from "@/components/SharePostRow";
import B2BMarketingAgenciesGuideLink from "@/components/B2BMarketingAgenciesGuideLink";
import { canonicalPath } from "@/lib/canonicalPath";
import ServiceHubAuditCard from "@/components/ServiceHubAuditCard";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";
import { ABM_HUB_FIRST_ANSWER, SCORE_LABEL, UK_DELIVERY_NOTE } from "@/lib/serviceHubContracts";

import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FaqClusterLoadingShell } from "@/components/FaqClusterLoadingShell";
import { FaqSearchControl } from "@/components/FaqSearchControl";
import { filterFaqItems } from "@/lib/faqSearch";
import {
  Users,
  Target,
  Clock,
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Building2,
  Shield,
  Briefcase,
  Monitor,
  Megaphone,
  Scale,
  Lock,
  Settings,
  Award,
  TrendingUp,
} from "lucide-react";

import SchemaScript from "@/components/SchemaScript";
/*
 * ─────────────────────────────────────────────────────────────────────────────
 * DESIGN: Signal & Authority, editorial brutalism meets Swiss typography
 * COLOR: Electric Amber #FF8C00 accent, charcoal #1C1917, warm off-white
 * TYPOGRAPHY: Instrument Serif (display) + DM Sans (body)
 * ─────────────────────────────────────────────────────────────────────────────
 */

const pageTitle = "Account-Based Marketing Agency London: AI-Powered ABM";
const pageDescription = "Account-based marketing agency in London: AI-powered ABM for complex B2B buying groups, with persona-mapped content, intent signals and outreach.";
const pageHeadline = "Account-Based Marketing Agency London for B2B Buying Groups";
const answerBlock = `${ABM_HUB_FIRST_ANSWER} Integrated.Social runs ABM from London for B2B teams with high-value accounts; start with a 30-minute call to agree the account list.`;
const faqSummary = "These 19 answers cover what an ABM agency does, how it differs from lead generation and outbound, how to choose a 1:1, 1:few or 1:many tier, where agentic AI helps and how results are measured. They also cover fintech and regulated accounts. In short, the tier follows account value, every buying-group role gets its own message and proof, and a named senior person signs off before anything reaches a buyer.";

const personas = [
  { icon: Briefcase, title: "Commercial / Revenue", desc: "CRO, VP Sales, VP Partnerships, owns pipeline targets and revenue accountability" },
  { icon: Monitor, title: "Technology / IT", desc: "CTO, VP Engineering, IT Director, evaluates integration, security, and scalability" },
  { icon: TrendingUp, title: "Sales / Business Development", desc: "Sales Directors, BDRs, Account Executives, need enablement and pipeline velocity" },
  { icon: Megaphone, title: "Marketing / Demand Gen", desc: "CMO, VP Marketing, Demand Gen, orchestrates campaigns and measures attribution" },
  { icon: Scale, title: "Finance / Procurement", desc: "CFO, VP Finance, Procurement, controls budget approval and ROI justification" },
  { icon: Shield, title: "Legal / Compliance", desc: "General Counsel, DPO, Compliance Officer, assesses regulatory and contractual risk" },
  { icon: Lock, title: "Security / Risk", desc: "CISO, InfoSec Director, evaluates data handling, vendor risk, and SOC 2 compliance" },
  { icon: Settings, title: "Operations / Enablement", desc: "COO, RevOps, Sales Enablement, ensures operational readiness and adoption" },
  { icon: Award, title: "Champion / Internal Sponsor", desc: "The internal advocate who builds consensus, navigates politics, and drives urgency" },
];

const abmModels = [
  {
    tier: "1:1 Strategic ABM",
    desc: "Bespoke campaigns for your top 5–15 named accounts. Fully personalized content, executive outreach, and account-specific landing pages.",
    fit: "Enterprise deals >$250K ACV with 6–12 month cycles",
  },
  {
    tier: "1:Few Cluster ABM",
    desc: "Shared campaigns for clusters of 15–50 accounts grouped by industry vertical, tech stack, or buying stage. Persona-specific messaging at scale.",
    fit: "Mid-market segments with shared pain points and similar buying committees",
  },
  {
    tier: "1:Many Programmatic ABM",
    desc: "AI-powered targeting of 100–500+ accounts using intent signals, firmographic filters, and automated multi-channel orchestration.",
    fit: "Volume pipeline generation with efficient cost-per-engaged-account",
  },
];

const deliveryPhases = [
  {
    phase: "Days 1–30: Foundation",
    items: [
      "ICP refinement and target account list (TAL) build",
      "Buying committee mapping per account tier",
      "Intent signal baseline from 6sense / Bombora / LinkedIn",
      "Content audit and gap analysis per persona",
      "Measurement framework and attribution model setup",
    ],
  },
  {
    phase: "Days 31–60: Activation",
    items: [
      "Persona-specific content production (whitepapers, case studies, POV pieces)",
      "Multi-channel campaign launch: LinkedIn Ads, programmatic display, email sequences",
      "Sales enablement assets and talk tracks per buying stage",
      "Account-specific landing pages for 1:1 tier",
      "Weekly pipeline review cadence with sales leadership",
    ],
  },
  {
    phase: "Days 61–90: Optimization",
    items: [
      "A/B testing of messaging, creative, and channel mix",
      "Account engagement scoring and progression analysis",
      "Pipeline influence attribution reporting",
      "Expansion of winning plays across account tiers",
      "Quarterly business review with executive stakeholders",
    ],
  },
];

const kpis = [
  { metric: "Account Engagement Rate", target: "Share of target accounts showing multi-channel engagement, compared with an agreed baseline" },
  { metric: "Buying Committee Coverage", target: "Relevant buying roles engaged per target account, compared with the account plan" },
  { metric: "Pipeline Influence", target: "ABM-sourced or ABM-influenced pipeline as % of total" },
  { metric: "Deal Velocity", target: "Reduction in average sales cycle length vs. non-ABM deals" },
  { metric: "Win Rate", target: "Improvement in close rate for ABM-targeted accounts" },
  { metric: "Average Deal Size", target: "Increase through multi-stakeholder engagement" },
];

const faqs = [
  { q: "What is an account-based marketing agency?", a: ABM_HUB_FIRST_ANSWER },
  {
    q: "Our sales team says marketing is sending the wrong leads. How do we fix that?",
    a: "This is a common friction between B2B sales and marketing teams, and targeting is one place to investigate. Marketing may be optimizing for MQL volume while sales is optimizing for deal quality. ABM starts with the accounts sales actually wants to win, builds campaigns around the buying committee and measures pipeline influence rather than lead volume. Agree account criteria, qualification rules and the sales handoff before launch; better opportunities are an objective, not a guaranteed result.",
  },
  {
    q: "We are running outbound but our best-fit accounts are not responding. What are we missing?",
    a: "Single-threaded outbound can miss the other people involved in an enterprise B2B decision. If you are reaching one person with one message on one channel, other buying-group members may never see relevant evidence. ABM maps the roles, their questions and the channels they use, then coordinates approved content and sales follow-up. The buying group and journey should be verified for your accounts rather than assumed from a generic benchmark.",
  },
  {
    q: "6sense found that 95% of eventual winners were on the shortlist from day one. How do we get on that shortlist?",
    a: "Be useful before the formal buying process starts. Make your offer, relevant proof and implementation constraints easy to find and verify. 6sense’s 2025 buyer research reports that the winning vendor was on the day-one shortlist in 95% of cases. That is a study finding, not a forecast for your accounts. We combine ABM with our SEO, AEO and GEO service to strengthen discoverability during supplier research; this cannot guarantee shortlist inclusion or an AI citation.",
  },
  {
    q: "What ABM models does Integrated.Social offer?",
    a: "We operate across all three ABM tiers: 1:1 Strategic ABM for your top 5–15 named accounts with fully bespoke campaigns; 1:Few Cluster ABM for groups of 15–50 accounts sharing similar characteristics; and 1:Many Programmatic ABM using AI-powered targeting for 100–500+ accounts. Most clients start with a blended approach, 1:1 for their top tier and 1:Many for pipeline volume, then expand as results compound.",
  },
  {
    q: "How does Agentic AI make ABM more effective without losing personalization quality?",
    a: "Our Gemini Enterprise Agentic AI environments support intent signal monitoring, content personalization per persona, multi-channel orchestration, engagement scoring and campaign optimization. Agents draft; a named senior person signs off. The team reviews account evidence, claims and proposed actions before activation rather than treating generated content as approved outreach. See our Agentic AI service for the underlying infrastructure.",
  },
  {
    q: "How is ABM success measured?",
    a: "Measure account-based marketing against agreed target accounts, engagement quality, sales acceptance and opportunity progression. Record the starting position and distinguish sourced pipeline from influenced pipeline. Revenue is a separate outcome, often realized later. Activity counts alone cannot show whether the program helped the sales team reach commercially relevant buyers.",
  },
  {
    q: "Can ABM work alongside our existing demand generation programs?",
    a: "Absolutely, and it should. ABM and demand gen are complementary, not competing. Demand gen fills the broader funnel and identifies accounts showing early intent signals. ABM then provides focused, personalized engagement for accounts that match your ICP and show buying readiness. We design integrated programs where demand gen insights feed ABM targeting, and ABM learnings improve demand gen efficiency. The result is a full-funnel approach that maximizes both volume and conversion.",
  },
  {
    q: "What does an ABM agency do?",
    a: "An ABM agency names the accounts, the buying roles, and the marketing that supports a sales conversation. It is not lead generation with a location page.",
  },
  { q: "What should an ABM agency in the UK actually run?", a: "The account-selection method, buying-committee map, channel plan, content plan, sales handoff, measurement approach and 90-day milestones. " + UK_DELIVERY_NOTE },
  { q: "What does a FinTech marketing agency in London do?", a: "A FinTech marketing agency helps payments, lending, wealth and B2B finance firms reach buyers through search, AI answers, account-based marketing and paid media, written with financial promotion rules in mind. Integrated.Social is a senior-led London option with published pricing and ABM experience; your compliance owner still approves every claim before it goes live." },
  { q: "How did Integrated.Social compare London FinTech marketing agencies?", a: "We checked London agencies whose own websites describe fintech marketing work and show a London address, on 20 October 2026. We scored whether each publishes a price (transparency, 40%), publicly stated senior-led delivery (35%) and a listed fintech service with a published London address (25%). On these criteria a fintech specialist scores above us; the table shows the computed order, sources and fetch dates." },
  { q: "How much does FinTech marketing cost in London?", a: "Published figures vary widely: on agencies' own sites we found a monthly retainer figure of £3,000 and a £60K project floor, and most show pricing on request. Integrated.Social offers the Foundation program, from £3,000 per month + VAT, and the AI Visibility Audit, from £750. Media spend sits outside agency fees." },
  { q: "When is a fintech specialist a better fit than Integrated.Social?", a: "If you want a fintech-only team with long sector tenure, a fintech specialist agency may suit you better; if you want fixed-fee brand and growth projects, choose a project-based studio. Consumer fintechs needing broad multi-channel scale may prefer a larger agency. We suit B2B fintechs wanting one senior lead across ABM, AI search and paid media." },
  { q: "Can account-based marketing work for a fintech, and who approves the claims?", a: "Yes, when you sell to named institutions or business buyers. ABM focuses research, content and paid media on named accounts and their buying groups. We flag claims needing evidence, but we do not give legal or compliance advice; your compliance owner approves every promotion." },
  { q: "How should we choose an ABM tier?", a: "Choose 1:1, 1:few or 1:many based on account value, evidence, buying complexity and delivery capacity rather than a default package. A scoped decision should follow the commercial problem." },
  {
    q: "Is 1:1 ABM the default?",
    a: "No. Most clients use a blend, and the tier should match account value. 1:1 is the strategic account, with its own landing page in the deliverables list. 1:few is the cluster with a shared pain. 1:many is the wider tier already described under that heading. A five-person committee receiving one generic message is the failure mode in the SaaS example, not a reason to buy 1:1 for every logo.",
  },
  {
    q: 'How should ABM adapt when buyers use AI tools to research suppliers?',
    a: 'Make core evaluation evidence easy to find and verify. A buyer or research assistant should be able to understand the offer, relevant proof, security and implementation constraints from public, current HTML without relying on a gated sales deck. The right evidence varies by sector and must be reviewed for accuracy and permission.',
  },
  {
    q: 'How can regulated B2B teams run account-based marketing responsibly?',
    a: 'Build the claims, data-use and approval rules into the program before outreach starts. For regulated sectors this usually means documented audience criteria, approved claims, clear ownership, appropriate consent and suppression controls, and human review for higher-risk communications. Legal and compliance teams should confirm the controls that apply to the organization and market.',
  }
];

export default function ServiceABM() {
  const [faqQuery, setFaqQuery] = useState("");
  const faqSearchId = useId().replace(/:/g, "");
  const filteredFaqs = useMemo(
    () => filterFaqItems(faqs, faqQuery, (faq) => faq.q, (faq) => faq.a),
    [faqQuery],
  );

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      ...getServiceSchemaSupportingEntities(),
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/account-based-marketing/#webpage",
        "url": "https://integrated.social/services/account-based-marketing/",
        "name": pageTitle,
        "headline": pageHeadline,
        "description": pageDescription,
        "abstract": answerBlock,
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/#organization" },
        "publisher": { "@id": "https://integrated.social/#organization" },
        "breadcrumb": { "@id": "https://integrated.social/services/account-based-marketing/#breadcrumb" },
        "mainEntity": { "@id": "https://integrated.social/services/account-based-marketing/#service" },
        "hasPart": { "@id": "https://integrated.social/services/account-based-marketing/#faq" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "inLanguage": "en-US",
        "dateModified": "2026-11-04"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/account-based-marketing/#service",
        "name": "Account-Based Marketing for Complex B2B Buying Groups",
        "mainEntityOfPage": { "@id": "https://integrated.social/services/account-based-marketing/#webpage" },
        "url": "https://integrated.social/services/account-based-marketing/",
        "description": "Account-based marketing for B2B companies with named accounts and complex buying groups. Persona research and approved messaging can also support B2C and ecommerce brands selling to business partners or institutions; this is not consumer account targeting. Agents draft; a named senior person signs off.",
        "provider": { "@id": "https://integrated.social/#organization" },
        "audience": [
          { "@type": "BusinessAudience", "audienceType": "B2B companies" },
          { "@type": "Audience", "audienceType": "B2C and ecommerce brands" }
        ],
        "areaServed": [
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Place", "name": "Global" }
        ],
        "serviceType": "Account-Based Marketing",
        "offers": {
          "@type": "Offer",
          "description": "90-day ABM deployment program with buying committee mapping, persona content, and multi-channel activation"
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/account-based-marketing/#faq",
        "isPartOf": { "@id": "https://integrated.social/services/account-based-marketing/#webpage" },
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/account-based-marketing/#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": pageTitle, "item": "https://integrated.social/services/account-based-marketing/" },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        ogImage="https://integrated.social/media/integrated-social-logo_c362b0f0.webp"
        description={pageDescription}
        canonical="https://integrated.social/services/account-based-marketing/"
      />
      <Header />
      <main className="min-h-screen bg-background">
        {/* Schema */}
        <SchemaScript schema={JSON.stringify(schemaData)} />

        {/* Breadcrumb */}
        <nav className="container max-w-6xl mx-auto px-4 pt-8 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link href="/#services" className="hover:text-foreground transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-foreground font-medium">Account-Based Marketing</li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="container max-w-6xl mx-auto px-4 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 px-3 py-1 rounded-sm mb-6">
                Are your best-fit accounts invisible to your pipeline?
              </span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] mb-6">
                {pageHeadline}
              </h1>
              <p className="subheading text-lg text-muted-foreground leading-relaxed mb-3">
                Your ideal accounts are out there. They just do not know you exist yet.
              </p>
              <p data-answer-block className="text-lg text-muted-foreground leading-relaxed mb-6">
                {answerBlock}
              </p>
              <ServiceHubAuditCard cluster="ABM" />
              {/* RTB strip */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="border border-[#00D4AA]/30 bg-[#00D4AA]/5 rounded-sm px-3 py-2 text-center">
                  <div className="text-[#00D4AA] font-bold text-base">+312%</div>
                  <div className="text-muted-foreground text-xs mt-0.5">Qualified pipeline in 90 days (ABM only)</div>
                </div>
                <div className="border border-border rounded-sm px-3 py-2 text-center">
                  <div className="text-foreground font-bold text-base">£2.4M</div>
                  <div className="text-muted-foreground text-xs mt-0.5">Pipeline in 6 months (ABM only)</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-[#FF8C00] text-white px-8 py-4 text-base font-medium rounded-sm hover:bg-[#E07B00] transition-colors" onClick={() => { trackCalendlyClick("service_abm"); trackSectionCtaClick({ section_name: "service_abm_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Book your free ABM Strategy Session →
                </a>
                <a
                  href="#abm-models"
                  className="inline-flex items-center justify-center border border-border text-foreground px-8 py-4 text-base font-medium rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors"
                >
                  See ABM models
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#FF8C00]/10 to-transparent rounded-lg p-8">
                <div className="grid grid-cols-3 gap-3">
                  {personas.slice(0, 9).map((p, i) => (
                    <div key={i} className="flex flex-col items-center text-center p-3 bg-card rounded-lg border border-border/50">
                      <p.icon className="w-5 h-5 text-[#FF8C00] mb-1" />
                      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{p.title.split(" / ")[0]}</span>
                    </div>
                  ))}
                </div>
                <p className="text-center text-xs text-muted-foreground mt-4">Illustrative buying roles; the mix depends on the account</p>
              </div>
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text={answerBlock}
            title="Account-Based Marketing Agency London"
            readingTime={5}
          />
        </div>

        {/* Evidence Section */}
        <section className="bg-muted/30 py-16 md:py-20">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              The Reality of Modern B2B Buying
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-3xl">
              Map the buying group from account evidence, not a universal stakeholder count or average journey. Commercial, technical, financial and risk roles can need different proof before the same purchase progresses.
            </p>
            <div className="mt-8 p-6 bg-card border border-[#FF8C00]/20 rounded-lg">
              <p className="text-foreground font-medium mb-2">The practical implication:</p>
              <p className="text-muted-foreground">
                Make relevant evaluation evidence available before formal outreach, then agree how sales will use account engagement signals. Presence and useful content can support consideration, but they do not guarantee a place on a shortlist.
              </p>
            </div>
          </div>
        </section>

        {/* Buying Committee Personas */}
        <section className="py-16 md:py-20" id="personas">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Buying Committee Personas We Target
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
              Every enterprise deal involves multiple stakeholders with different priorities, concerns, and decision criteria. 
              We create persona-specific content and engagement strategies for each role in the committee.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {personas.map((persona, i) => (
                <div key={i} className="group border border-border rounded-lg p-6 hover:border-[#FF8C00]/40 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-md bg-[#FF8C00]/10 flex items-center justify-center">
                      <persona.icon className="w-5 h-5 text-[#FF8C00]" />
                    </div>
                    <h3 className="font-medium text-foreground">{persona.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{persona.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ABM Models */}
        <section className="bg-muted/30 py-16 md:py-20" id="abm-models">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              ABM Models: 1:1, 1:Few, and 1:Many
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
              We operate across all three ABM tiers, and most clients deploy a blended approach that matches investment to account value.
            </p>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-card"><tr><th className="px-4 py-3 text-left">ABM Tier</th><th className="px-4 py-3 text-left">Ideal Account Profile</th><th className="px-4 py-3 text-left">Core Deliverables & Scope</th></tr></thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  {abmModels.map((model) => <tr key={model.tier}><td className="px-4 py-4 text-foreground font-semibold">{model.tier}</td><td className="px-4 py-4">{model.fit}</td><td className="px-4 py-4">{model.desc}</td></tr>)}
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">See how <Link href="/services/gemini-agentic-ai/" className="text-[#FF8C00] hover:underline">agentic AI</Link> supports intent monitoring, personalization, orchestration, and engagement scoring across the tiers. Agents draft; a named senior person signs off.</p>
          </div>
        </section>

        {/* 90-Day Delivery Framework */}
        <section className="py-16 md:py-20" id="delivery">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              90-Day ABM Delivery Framework
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
              We scope ABM programs in 90-day sprints, with agreed milestones, a named owner and pipeline measurement. The framework sets the work and review cadence; it does not promise pipeline within a fixed period.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {deliveryPhases.map((phase, i) => (
                <div key={i} className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#FF8C00] text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-foreground">{phase.phase}</h3>
                  </div>
                  <ul className="space-y-3">
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#FF8C00] mt-0.5 shrink-0" />
                        <span className="text-sm text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* KPIs */}
        <section className="bg-muted/30 py-16 md:py-20" id="kpis">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Account-Level KPIs and Measurement
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
              We measure what matters to revenue. Not vanity metrics. Every KPI ties back to pipeline and deal progression.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {kpis.map((kpi, i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-6">
                  <BarChart3 className="w-5 h-5 text-[#FF8C00] mb-3" />
                  <h3 className="font-medium text-foreground mb-2">{kpi.metric}</h3>
                  <p className="text-sm text-muted-foreground">{kpi.target}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Integrated.Social for ABM */}
        <section className="py-16 md:py-20">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Why Integrated.Social for ABM
            </h2>
            <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
              ABM is a B2B approach to named accounts and complex buying groups. Persona research and approved messaging can also support B2C and ecommerce brands selling to business partners or institutions; this is not consumer account targeting.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "Experience in Complex B2B",
                  desc: "We have worked with enterprise technology, fintech, SaaS, and regulated industries. The account plan starts with the buying roles, evidence needs and approval constraints of your market.",
                },
                {
                  title: "Agentic AI at Scale",
                  desc: "Our Gemini Enterprise AI environments support multi-tier ABM, intent monitoring, content personalization and channel orchestration. Agents draft; a named senior person signs off.",
                  link: "/services/gemini-agentic-ai",
                  linkText: "Learn about our Agentic AI →",
                },
                {
                  title: "AI Search Visibility Built In",
                  desc: "We integrate ABM with SEO + AEO/GEO to strengthen discoverability when buying committee members research solutions. Rankings, AI citations and shortlist inclusion are not guaranteed.",
                  link: "/services/seo-aeo-geo",
                  linkText: "See our SEO/AEO/GEO service →",
                },
                {
                  title: "Performance Marketing Precision",
                  desc: "ABM campaigns are backed by structured A/B testing, Performance Max optimization, and persona-specific PPC. Not spray-and-pray advertising.",
                  link: "/services/ppc-performance-max",
                  linkText: "See our PPC service →",
                },
              ].map((item, i) => (
                <div key={i} className="border border-border rounded-lg p-8 hover:border-[#FF8C00]/40 transition-colors">
                  <h3 className="font-serif text-xl font-bold text-foreground mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-4">{item.desc}</p>
                  {item.link && (
                    <Link href={canonicalPath(item.link)} className="text-sm font-medium text-[#FF8C00] hover:underline">
                      {item.linkText}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-links to Related Services */}
        <section className="bg-muted/30 py-16 md:py-20">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-2xl font-bold text-foreground mb-8">
              Related AI Marketing Services
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { href: "/services/gemini-agentic-ai/", title: "Gemini Enterprise Agentic AI", desc: "Multi-agent environments for marketing automation" },
                { href: "/services/seo-aeo-geo/", title: "SEO + AEO/GEO", desc: "AI search visibility and answer engine optimization" },
                { href: "/services/ppc-performance-max/", title: "PPC & Performance Max", desc: "AI-powered paid media with structured A/B testing" },
                { href: "/services/ai-marketing-strategy/", title: "AI Marketing Strategy", desc: "GTM strategy and content operations at scale" },
              ].map((service, i) => (
                <Link key={i} href={service.href} className="group block border border-border rounded-lg p-5 hover:border-[#FF8C00]/40 transition-colors">
                  <h3 className="font-medium text-foreground group-hover:text-[#FF8C00] transition-colors mb-2 text-sm">{service.title}</h3>
                  <p className="text-xs text-muted-foreground">{service.desc}</p>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#FF8C00] mt-3 transition-colors" />
                </Link>
              ))}
            </div>
            <B2BMarketingAgenciesGuideLink headingLevel="p" />
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="font-serif text-2xl font-bold text-foreground mb-2">
              From the ABM &amp; AI Marketing Blog
            </h2>
            <p className="text-muted-foreground text-sm mb-8">Evidence-led guides on AI-powered ABM, buying committee strategy, and B2B pipeline growth.</p>

            {/* Featured article: AI Agents replacing B2B research phase */}
            <Link
              href="/blog/ai-agents-replacing-b2b-research-phase/"
              className="group block border border-[#FF8C00]/30 rounded-xl p-7 mb-8 hover:border-[#FF8C00]/70 transition-colors bg-[#FF8C00]/[0.04] relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-[#FF8C00] rounded-l-xl" />
              <div className="pl-2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/40 rounded-full">
                    New: Agentic AI
                  </span>
                  <span className="text-xs text-muted-foreground">Published June 27, 2026</span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-bold text-foreground group-hover:text-[#FF8C00] transition-colors leading-snug mb-3">
                  How AI Agents Are Replacing the B2B Research Phase Before Vendors Are Contacted
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-3xl">
                  Explore how AI assistants can support shortlisting, vendor comparison and research before a buyer contacts sales. The practical task for ABM teams is to make relevant evaluation evidence easy to retrieve and verify, without assuming that AI visibility guarantees a place on the shortlist.
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-[#FF8C00] font-semibold">
                  Read the full analysis <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Supporting articles grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  href: "/blog/ai-powered-abm-enterprise-b2b-2026/",
                  tag: "ABM Strategy",
                  title: "How to Build an AI-Powered ABM Program for Enterprise B2B in 2026",
                  desc: "A step-by-step framework for AI-assisted account selection, buying committee mapping and personalized multi-channel sequences, measured against agreed account and pipeline evidence.",
                },
                {
                  href: "/blog/chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026/",
                  tag: "Agentic AI",
                  title: "ChatGPT Workspace Agents Are Rewriting the B2B Dark Funnel",
                  desc: "How AI agents conduct vendor research and shortlist suppliers before any human enters the conversation - and what ABM teams must do now.",
                },
                {
                  href: "/blog/multi-agent-ai-automate-gtm-2026/",
                  tag: "GTM Automation",
                  title: "How to Use Multi-Agent AI to Automate Your Entire GTM Motion",
                  desc: "A practical breakdown of a multi-agent GTM engine that supports ICP research, persona mapping, content drafting and pipeline reporting, with human sign-off.",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  href={article.href}
                  className="group block border border-border rounded-xl p-6 hover:border-[#FF8C00]/40 transition-colors bg-card"
                >
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-4">
                    {article.tag}
                  </span>
                  <h3 className="font-semibold text-foreground group-hover:text-[#FF8C00] transition-colors text-sm leading-snug mb-3">
                    {article.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{article.desc}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#FF8C00] font-medium">
                    Read article <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
            <B2BMarketingAgenciesGuideLink headingLevel="p" />
          </div>
        </section>

        {/* See it in action, Case Study Feature Block */}
        <section className="py-20 bg-[#0f0f0f] text-white overflow-hidden">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: narrative */}
              <div>
                <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
                  See it in action
                </span>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
                  Two personas. One product. A persona-led campaign that supported a funding round.
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  A UK personal finance app was failing because it was targeting one audience with one message. The audience had two distinct personas: the <strong className="text-white">Rational Planner</strong> (analytical, spreadsheet-driven, ROI-focused) and the <strong className="text-white">Aspirational Fashionista</strong> (lifestyle-led, social-proof-driven, identity-focused). By rebuilding the entire acquisition funnel around each persona’s decision triggers, running a structured A/B testing program and deploying persona-specific creative, the campaign supported a multi-million pound funding round. This is a B2C persona example, not an ABM result or evidence that marketing alone secured the funding.
                </p>
                <blockquote className="border-l-2 border-[#FF8C00] pl-4 text-gray-300 italic mb-8">
                  &ldquo;ABM is not about targeting accounts. It is about understanding every person in the buying group, what they care about, and what will move them from awareness to action. The Rational Planner and the Fashionista were both right, they just needed completely different conversations.&rdquo;
                  <footer className="text-sm text-gray-500 mt-2 not-italic">- Modi Elnadi, Integrated.Social</footer>
                </blockquote>
                <Link
                  href="/case-studies/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-semibold rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]"
                >
                  Read the full case study <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {/* Right: persona + result cards */}
              <div className="space-y-4">
                {/* Persona cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/[0.04] border border-[#00D4AA]/20 rounded-xl p-5 hover:border-[#00D4AA]/40 transition-colors">
                    <div className="text-[#00D4AA] text-xs font-mono uppercase tracking-widest mb-2">Persona 01</div>
                    <div className="text-white font-semibold text-base mb-1">Rational Planner</div>
                    <div className="text-gray-500 text-xs leading-relaxed">Analytical · ROI-driven · Spreadsheet-first · Needs data proof</div>
                  </div>
                  <div className="bg-white/[0.04] border border-[#FF8C00]/20 rounded-xl p-5 hover:border-[#FF8C00]/40 transition-colors">
                    <div className="text-[#FF8C00] text-xs font-mono uppercase tracking-widest mb-2">Persona 02</div>
                    <div className="text-white font-semibold text-base mb-1">Aspirational Fashionista</div>
                    <div className="text-gray-500 text-xs leading-relaxed">Lifestyle-led · Social proof · Identity-focused · Needs aspiration</div>
                  </div>
                </div>
                {/* Result cards */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: "Multi-£M", label: "VC Round Secured", sub: "post-campaign", accent: "#FF8C00" },
                  ].map((card, i) => (
                    <div
                      key={i}
                      className="bg-white/[0.04] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors"
                    >
                      <div className="text-2xl font-bold mb-1" style={{ color: card.accent }}>{card.value}</div>
                      <div className="text-white text-sm font-semibold mb-1">{card.label}</div>
                      <div className="text-gray-500 text-xs">{card.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* See it in action, B2B SaaS ABM Case Study */}
        <section className="py-20 bg-[#0D1220] text-white overflow-hidden">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: narrative */}
              <div>
                <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-teal-400 border border-teal-400/30 rounded-full mb-6">
                  See it in action
                </span>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
                  Five-persona buying committee: +312% qualified pipeline in 90 days and £2.4M pipeline in 6 months
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  A B2B SaaS infrastructure provider had a pipeline that had stalled.
                  The problem was not the product, it
                  was a single generic message being sent to a five-person buying committee where every
                  stakeholder had fundamentally different priorities. By rebuilding the ICP, mapping
                  the full buying committee, and deploying persona-specific content sequences across
                  LinkedIn, email, and programmatic, the campaign generated +312% qualified pipeline in 90 days and £2.4M pipeline in 6 months (ABM only).
                </p>
                <blockquote className="border-l-2 border-teal-400 pl-4 text-gray-300 italic mb-8">
                  &ldquo;In complex B2B, the CFO and the Head of Engineering are both in the room -
                  but they are solving completely different problems. ABM only works when you have
                  a distinct message, a distinct proof point, and a distinct CTA for each of them.&rdquo;
                  <footer className="text-sm text-gray-500 mt-2 not-italic">- Modi Elnadi, Integrated.Social</footer>
                </blockquote>
                <Link
                  href="/case-studies/b2b-saas-abm/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-black font-semibold rounded-lg hover:bg-teal-400 transition-all duration-200 active:scale-[0.97]"
                >
                  Read the full case study <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {/* Right: buying committee + result cards */}
              <div className="space-y-4">
                {/* Buying committee personas */}
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { role: "VP of Engineering / CTO", focus: "Technical fit · Security · Integration complexity", color: "teal" },
                    { role: "Head of Finance / CFO", focus: "ROI · Payback period · Contract risk", color: "amber" },
                    { role: "Head of Product", focus: "Roadmap alignment · API quality · Scalability", color: "teal" },
                    { role: "Commercial Director", focus: "Revenue impact · Competitive advantage · Speed", color: "amber" },
                    { role: "Legal & Compliance", focus: "Data residency · SLA · Regulatory exposure", color: "teal" },
                  ].map((p) => (
                    <div key={p.role} className={`bg-white/[0.04] border ${
                      p.color === 'teal' ? 'border-teal-500/20 hover:border-teal-500/40' : 'border-amber-500/20 hover:border-amber-500/40'
                    } rounded-lg px-4 py-3 flex items-start gap-3 transition-colors`}>
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                        p.color === 'teal' ? 'bg-teal-400' : 'bg-amber-400'
                      }`} />
                      <div>
                        <div className="text-white text-sm font-semibold">{p.role}</div>
                        <div className="text-gray-500 text-xs mt-0.5">{p.focus}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Result metrics */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {[
                    { value: "+312%", label: "Qualified pipeline in 90 days (ABM only)", accent: "#2DD4BF" },
                    { value: "£2.4M", label: "Pipeline in 6 months (ABM only)", accent: "#F59E0B" },
                  ].map((card) => (
                    <div key={card.label} className="bg-white/[0.04] border border-white/10 rounded-xl p-4 text-center hover:border-teal-500/30 transition-colors">
                      <div className="text-2xl font-bold mb-1" style={{ color: card.accent }}>{card.value}</div>
                      <div className="text-gray-400 text-xs leading-tight">{card.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="fintech" className="border-y border-border bg-accent/20 py-16 md:py-20"><div className="container max-w-5xl mx-auto px-4"><p className="font-mono text-xs uppercase tracking-widest text-[#FF8C00]">Financial services</p><h2 className="mt-3 font-serif text-3xl md:text-4xl">FinTech marketing agency London: ABM, AI search and paid media</h2><p className="mt-5 max-w-4xl text-muted-foreground leading-relaxed">A FinTech marketing agency helps payments, lending, wealth and B2B finance firms reach buyers through search, AI answers, account-based marketing and paid media, written with financial promotion rules in mind. Integrated.Social is a senior-led London option with published pricing and ABM experience; your compliance owner still approves every claim before it goes live.</p><div className="mt-8 grid gap-6 md:grid-cols-2"><div><h3 className="font-serif text-2xl">FinTech content marketing</h3><p className="mt-3 text-muted-foreground leading-relaxed">Fintech buyers check claims carefully, so content has to be specific and approved. We build a short library around the questions your buyers and their risk teams actually ask: how the product works, what it costs to adopt, how data is handled and where it fits alongside existing providers. Each piece goes to your compliance owner before publication. Agents draft; a named senior person signs off, and your compliance approver has the final word.</p></div><div><h3 className="font-serif text-2xl">SEO and AI search for financial services</h3><p className="mt-3 text-muted-foreground leading-relaxed">Search and AI answers often shape a fintech shortlist before sales hears about it. We check that your priority pages are crawlable, answer buyer questions in the first paragraph and describe your firm consistently, then track a fixed prompt panel across ChatGPT, Perplexity, Gemini and Google AI Overviews. That shows where you appear and where you do not; it is not a promise of rankings, citations or leads.</p></div></div><div className="mt-8 overflow-x-auto"><table className="w-full min-w-[680px] text-sm"><caption className="mb-3 text-left text-muted-foreground">Integrated.Social publishes this comparison and is one of the agencies in it. It is our own comparison, not an independent review or a ranking of the best agencies. Criteria: published price (40%), publicly stated senior-led delivery (35%), and a listed fintech service with a published London address (25%). Recheck source pages before selecting a partner.</caption><thead><tr className="border-b border-border text-left"><th scope="col" className="p-3">Agency</th><th scope="col" className="p-3">Published scope</th><th scope="col" className="p-3">Published entry point</th></tr></thead><tbody className="text-muted-foreground"><tr className="border-b border-border"><th scope="row" className="p-3 text-foreground">Curious Cat Digital</th><td className="p-3">Fintech Marketing Agency</td><td className="p-3">Retainers typically start from £3,000 per month</td></tr><tr className="border-b border-border"><th scope="row" className="p-3 text-foreground">Integrated.Social</th><td className="p-3">ABM, AI search and paid media</td><td className="p-3">AI Visibility Audit from £750; Foundation program from £3,000 per month + VAT</td></tr><tr className="border-b border-border"><th scope="row" className="p-3 text-foreground">Florin</th><td className="p-3">London growth agency for fintechs</td><td className="p-3">From £60K project floor</td></tr><tr className="border-b border-border"><th scope="row" className="p-3 text-foreground">Found</th><td className="p-3">Fintech Digital Marketing Agency London, UK</td><td className="p-3">Price not published</td></tr><tr className="border-b border-border"><th scope="row" className="p-3 text-foreground">Growth Gorilla</th><td className="p-3">Fintech &amp; Financial Services Growth Marketing</td><td className="p-3">Price not published</td></tr><tr><th scope="row" className="p-3 text-foreground">Impression</th><td className="p-3">Fintech Marketing Agency | London &amp; Nottingham</td><td className="p-3">Price not published</td></tr></tbody></table></div><p className="mt-5 text-sm text-muted-foreground">A fintech specialist can be a better fit when you need long sector tenure or a larger multi-channel team. This page does not provide legal or compliance advice; your compliance owner approves every promotion.</p><p className="mt-5 text-sm"><Link href="/free-ai-growth-audit/" className="text-[#FF8C00] hover:underline">AI Visibility</Link> for a high-level starting point, or read the <Link href="/case-studies/fintech-persona-targeting/" className="text-[#FF8C00] hover:underline">B2C fintech case study</Link>.</p></div></section>
        {/* FAQ */}
        <section className="py-16 md:py-20" id="faq">
          <div className="container max-w-4xl mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4 text-center">
              ABM Frequently Asked Questions
            </h2>
            <p data-faq-summary className="text-muted-foreground text-center mb-12">
              {faqSummary}
            </p>
          <p className="text-sm text-white/60 mb-6">Source context for FAQ 4: <a href="https://6sense.com/science-of-b2b/getting-to-yes-why-vendors-win-and-how-buying-groups-agree/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] underline">6sense’s 2025 Getting to Yes buyer research</a> reports a day-one shortlist finding, not a guarantee for an individual ABM program.</p>
            <FaqSearchControl
              query={faqQuery}
              onQueryChange={setFaqQuery}
              totalCount={faqs.length}
              resultCount={filteredFaqs.length}
              resultsId={`${faqSearchId}-faq-search`}
            />
            <FaqClusterLoadingShell itemCount={faqs.length}>
              <Accordion id={`${faqSearchId}-faq-search-results`} type="single" collapsible className="w-full">
                {filteredFaqs.map(({ item: faq, index }) => (
                  <AccordionItem key={faq.q} value={`faq-${index}`}>
                    <h3 className="m-0">
                      <AccordionTrigger className="text-left font-medium text-foreground hover:text-[#FF8C00]">
                        {faq.q}
                      </AccordionTrigger>
                    </h3>
                    <AccordionContent forceMount className="text-muted-foreground leading-relaxed data-[state=closed]:hidden">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {filteredFaqs.length === 0 ? (
                <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No FAQ answer matches that search. Try a different phrase or clear the search.
                </p>
              ) : null}
            </FaqClusterLoadingShell>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#1C1917] py-16 md:py-20">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Ready to Win Complex Buying Committees?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Book a 30-minute ABM strategy session with Modi Elnadi. We will map your target accounts, 
              identify buying committee gaps, and outline a 90-day deployment plan.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-[#FF8C00] text-white px-8 py-4 text-base font-medium rounded-sm hover:bg-[#E07B00] transition-colors" onClick={() => { trackCalendlyClick("service_abm"); trackSectionCtaClick({ section_name: "service_abm_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
              >
                Book ABM Strategy Session →
              </a>
              <a
                href="https://wa.me/447717804077"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border border-white/30 text-white px-8 py-4 text-base font-medium rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors"
              >
                WhatsApp Modi directly
              </a>
            </div>
            <p className="text-white/40 text-sm mt-6">
              Or email <a aria-label="Email Integrated.Social at info@integrated.social" href="mailto:info@integrated.social" className="text-[#FF8C00] hover:underline">info@integrated.social</a>
            </p>
          </div>
        </section>
      </main>
      <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      <Footer />

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Start with the Free AI Visibility Score for a high-level baseline across broad dimensions. It does not promise citations, rankings, leads, pipeline or revenue.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_abm_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  {SCORE_LABEL}
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_abm"); trackSectionCtaClick({ section_name: "service_abm_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Account-Based Marketing (ABM)"
          canonicalUrl="https://integrated.social/services/account-based-marketing/"
          excerpt="Precision ABM programs that align sales and marketing around your highest-value accounts."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
